import { authenticatedFetch } from '@beholos/holoscommon';
import type { ClassInterface } from '../types/class.interface';
import type { CountryInterface } from '../types/country.interface';
import { API_BASE_URL } from './constants';
import { trackEvent } from './analytics.service';

export interface ScheduleFilters {
  page?: number;
  limit?: number;
  name?: string;
  categories?: string | string[];
  studios?: string | string[];
  liveStreamClass?: boolean;
  date?: string;
  start?: string;
  passes?: number;
  showOnlyOneOnOne?: boolean;
  cities?: string | string[];
  districts?: string | string[];
  countries?: string | string[];
  showNutritionist?: boolean;
  showHealthCoach?: boolean;
  showFinancialAdvisor?: boolean;
  showPsychologist?: boolean;
  showPersonalTrainer?: boolean;
  inPlan?: boolean;
}

export interface ScheduleResponse {
  _id: string;
  id?: string; // Add optional id field
  title: string;
  description: string;
  formattedDescription: string;
  startDate: string;
  endDate: string;
  timeStamp?: number;
  timeStampEnd?: number;
  repeat: {
    days: string[];
    start: string;
    end: string;
    _id: string;
  };
  teachers: string;
  vacancies: number;
  category: Array<{
    _id: string;
    name: string;
    image: string;
    active: boolean;
  }>;
  studio: {
    _id: string;
    name: string;
    slug?: string;
    district: {
      _id: string;
      name: string;
      active: boolean;
    };
    services: Array<{
      _id: string;
      name: string;
      image: string;
    }>;
    premium: boolean;
    hasLiveStream: boolean;
    rating: number;
    ratingCount: number;
    passes: number;
    image: string;
    description: string;
    address: {
      latitude: number;
      longitude: number;
      street: string;
      state: string;
      zip: string;
    };
    country?: {
      _id: string;
      name: string;
      image: string;
    };
    city?: {
      _id: string;
      name: string;
    };
    tier?: 'LEVEL_1' | 'LEVEL_2' | 'LEVEL_3' | 'LEVEL_4' | 'LEVEL_5' | 'LEVEL_6' | null
  };
  active: boolean;
  intensity: number;
  passes: number;
  enrollTimeRestriction: number;
  cancelTimeRestriction: number;
  individualSession: boolean;
  corporate: boolean;
  slug: string;
  holidays: any[];
  equipment: any[];
  isPartnerClosed?: boolean;
  partnerClosedReason?: string;
}

export interface PartnerResponse {
  _id: string;
  name: string;
  slug: string;
  country: CountryInterface,
  otherStudioImages: string[]
}

export interface CategoryResponse {
  _id: string;
  name: string;
  image?: string;
  slug?: string;
}

export interface LocationResponse {
  countries: Array<{
    id: string;
    name: string;
    image: string;
  }>;
  cities: Array<{
    id: string;
    name: string;
  }>;
  districts: Array<{
    id: string;
    _id?: string;
    name: string;
  }>;
}

// Transform API response to match our ClassInterface
const transformScheduleToClass = (schedule: ScheduleResponse): ClassInterface => {
  // Handle both _id and id fields from the API response
  const scheduleId = schedule._id || schedule.id || '';

  // Read optional country and city nested on studio if provided by API
  const studioCountry: any = (schedule.studio as any)?.country;
  const studioCity: any = (schedule.studio as any)?.city;
  const studioId: string = (schedule.studio as any)?._id || (schedule.studio as any)?.id || '';

  // Normalize country image to CDN when provided as a relative path
  const rawCountryImage: string | undefined = studioCountry?.image || undefined;
  const normalizedCountryImage = `https://cache.beholos.com${rawCountryImage || ''}`;

  return {
    _id: scheduleId,
    id: scheduleId,
    title: schedule.title,
    description: schedule.description,
    formattedDescription: schedule.formattedDescription,
    repeat: schedule.repeat,
    category: schedule.category,
    studio: {
      _id: studioId,
      name: schedule.studio.name,
      district: schedule.studio.district,
      tier: schedule.studio?.tier || 'LEVEL_1',
      address: {
        city: (schedule.studio.address as any)?.city || schedule.studio.address.state,
        state: schedule.studio.address.state,
        street: schedule.studio.address.street,
        latitude: schedule.studio.address.latitude,
        longitude: schedule.studio.address.longitude,
        zip: schedule.studio.address.zip
      },
      image: schedule.studio.image,
      description: schedule.studio.description,
      formattedDescription: schedule.studio.description,
      email: '',
      active: true,
      slug: schedule.studio.slug || schedule.studio.name.toLowerCase().replace(/\s+/g, '-'),
      services: schedule.studio.services.map(service => ({
        id: service._id,
        name: service.name,
        image: service.image
      })),
      businessHours: '',
      howToPrepare: '',
      otherStudioImages: [],
      studioVisitLimit: 0,
      howToGetThere: '',
      premium: schedule.studio.premium,
      rating: schedule.studio.rating,
      ratingCount: schedule.studio.ratingCount,
      hasLiveStream: schedule.studio.hasLiveStream,
      country: studioCountry ? {
        name: studioCountry.name,
        image: normalizedCountryImage,
      } : undefined,
      partnerType: 'studio',
      healthAndSafetyPolicy: '',
      passes: schedule.studio.passes,
      city: studioCity ? {
        name: studioCity.name,
      } : undefined,
      id: studioId
    },
    active: schedule.active,
    slug: schedule.slug,
    timeStamp: schedule.timeStamp || 0,
    timeStampEnd: schedule.timeStampEnd || 0,
    vacancies: schedule.vacancies,
    isFavorite: (schedule as any).isFavourite ?? (schedule as any).isFavorite ?? false,
    isPartnerClosed: (schedule as any).partnerClosed ?? (schedule as any).isPartnerClosed ?? false,
    passes: schedule.passes,
    promotionalTier: (schedule as any).promotionalTier,
    enrollTimeRestriction: schedule.enrollTimeRestriction,
    cancelTimeRestriction: schedule.cancelTimeRestriction,
    // Prefer schedule-level liveStreamClass when present; fallback to studio flag
    liveStreamClass: (Object.prototype.hasOwnProperty.call(schedule as any, 'liveStreamClass')
      ? !!(schedule as any).liveStreamClass
      : !!schedule.studio.hasLiveStream),
    liveStreamPassword: undefined,
    partnerClosedReason: (schedule as any).partnerClosedReason,
  };
};

// Build query string from filters
const buildQueryString = (filters: ScheduleFilters): string => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        const arrayValue = value.join(',');
        params.append(key, arrayValue);
        if (key === 'districts') {

        }
      } else {
        const stringValue = String(value);
        params.append(key, stringValue);
      }
    }
  });

  const queryString = params.toString();
  if (queryString.includes('districts=')) {

  }
  return queryString;
};

// Fetch schedules with filters
export const fetchSchedules = async (filters: ScheduleFilters = {}): Promise<ClassInterface[]> => {
  try {
    const queryString = buildQueryString(filters);
    const url = `${API_BASE_URL}/schedules${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`);
    }

    const data: ScheduleResponse[] = await response.json();

    const classes = data.map(transformScheduleToClass).map(cls => {
      // Align class online flag with the same boolean used by filters
      if (typeof filters.liveStreamClass === 'boolean') {
        return {
          ...cls,
          isOnline: filters.liveStreamClass === true,
          liveStreamClass: filters.liveStreamClass
        } as ClassInterface;
      }
      return cls;
    });

    return classes;
  } catch (error) {
    console.error('Error fetching schedules:', error);
    throw error;
  }
};

// Fetch online schedules specifically
export const fetchOnlineSchedules = async (filters: Omit<ScheduleFilters, 'liveStreamClass'> = {}): Promise<ClassInterface[]> => {
  try {
    const queryString = buildQueryString(filters);
    const url = `${API_BASE_URL}/schedules/online${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ScheduleResponse[] = await response.json();

    return data.map(transformScheduleToClass);
  } catch (error) {
    console.error('Error fetching online schedules:', error);
    throw error;
  }
};

// Fetch partners for the filter dropdown
export const fetchPartners = async ({
  showNutritionist = false,
  showHealthCoach = false,
  showFinancialAdvisor = false,
  showPsychologist = false,
  showPersonalTrainer = false,
  inPlan = false
}: {
  showNutritionist?: boolean;
  showHealthCoach?: boolean;
  showFinancialAdvisor?: boolean;
  showPsychologist?: boolean;
  showPersonalTrainer?: boolean;
  inPlan?: boolean;
}): Promise<PartnerResponse[]> => {
  try {
    const queryParams = new URLSearchParams({
      showNutritionist: String(showNutritionist),
      showHealthCoach: String(showHealthCoach),
      showFinancialAdvisor: String(showFinancialAdvisor),
      showPsychologist: String(showPsychologist),
      showPersonalTrainer: String(showPersonalTrainer),
      inPlan: String(inPlan), 
    });

    const response = await authenticatedFetch(`${API_BASE_URL}/partners/type?${queryParams}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: PartnerResponse[] = await response.json();

    return data;
  } catch (error) {
    console.error('Error fetching partners:', error);
    throw error;
  }
};

// Fetch categories for the filter dropdown
export const fetchCategories = async (): Promise<CategoryResponse[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/partner-offerings/categories`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: CategoryResponse[] = await response.json();

    return data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Fetch locations for the filter dropdown
export const fetchLocations = async (): Promise<LocationResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/locations`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: LocationResponse = await response.json();

    return data;
  } catch (error) {
    console.error('Error fetching locations:', error);
    throw error;
  }
};

// Fetch a specific schedule by ID
export const fetchScheduleById = async (id: string, date?: string): Promise<ClassInterface | null> => {
  try {
    const params = new URLSearchParams();
    if (date) {
      params.append('date', date);
    }

    const response = await authenticatedFetch(`${API_BASE_URL}/schedules/${id}${params.toString() ? `?${params.toString()}` : ''}`);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ScheduleResponse = await response.json();

    return transformScheduleToClass(data);
  } catch (error) {
    console.error('Error fetching schedule by ID:', error);
    throw error;
  }
};

// Add class to favorites
export const addToFavorites = async (classId: string): Promise<boolean> => {
  try {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('token') : null;
    const response = await authenticatedFetch(`${API_BASE_URL}/user/favorites/${classId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });

    if (response.ok) {

      return true;
    } else {
      console.error('Failed to add to favorites');
      return false;
    }
  } catch (error) {
    console.error('Error adding to favorites:', error);
    return false;
  }
};

// Remove class from favorites
export const removeFromFavorites = async (classId: string): Promise<boolean> => {
  try {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('token') : null;
    const response = await authenticatedFetch(`${API_BASE_URL}/user/favorites/${classId}`, {
      method: 'DELETE',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });

    if (response.ok) {

      return true;
    } else {
      console.error('Failed to remove from favorites');
      return false;
    }
  } catch (error) {
    console.error('Error removing from favorites:', error);
    return false;
  }
};

export const fetchUserFavorites = async (): Promise<any[]> => {
  try {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('token') : null;
    const response = await authenticatedFetch(`${API_BASE_URL}/user/favorites`, {
      headers: {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });

    if (response.ok) {
      const data = await response.json();
      // Support both array and wrapped response
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.favorites)) return data.favorites;
      return [];
    } else {
      try {
        const errText = await response.text();
        console.error('Failed to fetch user favorites', response.status, errText);
      } catch {
        console.error('Failed to fetch user favorites', response.status);
      }
      return [];
    }
  } catch (error) {
    console.error('Error fetching user favorites:', error);
    return [];
  }
};

// Enroll user in a class
export interface EnrollClassRequest {
  classId: string;
  startday: number;
  endday: number;
  isClassProfile: boolean;
}

export interface EnrollResponse {
  success: boolean;
  data?: {
    userClassId?: string;
    userClassHash?: {
      classHash?: string;
    };
    hash?: string;
  };
  error?: string;
}

export const enrollInClass = async (enrollmentData: EnrollClassRequest): Promise<EnrollResponse> => {
  try {
    console.log('Making API call to enroll with data:', enrollmentData);

    const response = await authenticatedFetch(`${API_BASE_URL}/user-class`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(enrollmentData)
    });

    console.log('Response status:', response.status);

    if (response.ok) {
      const responseData = await response.json();
      console.log('Enrollment successful:', responseData);
      trackEvent('class_reservation', { class_id: enrollmentData.classId });
      return {
        success: true,
        data: responseData
      };
    } else {
      let errorMessage = 'Ha ocurrido un error inesperado';

      try {
        const errorData = await response.json();
        // Try to extract meaningful error message from API response
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // If JSON parsing fails, try text
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        } catch {
          // Use default error message
        }
      }

      console.error('Failed to enroll in class. Status:', response.status, 'Error:', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  } catch (error) {
    console.error('Error enrolling in class:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error de conexión'
    };
  }
};

export const inviteFriendToClass = async (email: string, userClassId: string): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/user-class/invite`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        userClass: userClassId
      })
    });

    // Check if the response is ok first
    if (response.ok) {
      // Try to parse JSON, but handle cases where response might not be JSON
      try {
        const data = await response.json();
        return {
          success: true,
          message: data.message || 'Tu Amigo recibirá la invitación en su correo y su clase no será agendada hasta que acepte en su cuenta.'
        };
      } catch (jsonError) {
        // If JSON parsing fails but response is ok, assume success
        return {
          success: true,
          message: 'Tu Amigo recibirá la invitación en su correo y su clase no será agendada hasta que acepte en su cuenta.'
        };
      }
    } else {
      // Handle error responses
      let errorMessage = `Error ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If error response is not JSON, try to get text
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        } catch {
          // Use the default error message
        }
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  } catch (error) {
    console.error('Error inviting friend to class:', error);
    return {
      success: false,
      error: 'Error de conexión. Por favor intenta de nuevo.'
    };
  }
}; 